import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle, ChevronLeft, FileSpreadsheet, Loader } from 'lucide-react';
import toast from 'react-hot-toast';
import axiosInstance from '../api/axios';
import { uploadFilesAndComplete } from '../utils/fileUpload';
import { MODULE_UPLOAD } from '../utils/moduleUpload.ts';
import { resolve403ErrorMessage } from '../request/getApiErrorMessage.ts';
import { soxProcessCreatePath } from '../routing/processNavigation.ts';

const SUPPORTED_EXTENSIONS = ['xlsx', 'csv'];
const normalizeHeaderKey = (key) =>
  String(key || '')
    .toLowerCase()
    .replace(/[\s_-]+/g, '');

const getFirstValue = (row, keys) => {
  if (!row || typeof row !== 'object') return '';
  for (const key of keys) {
    if (row[key] !== undefined && row[key] !== null) {
      return row[key];
    }
  }
  const entries = Object.entries(row);
  for (const key of keys) {
    const want = normalizeHeaderKey(key);
    const hit = entries.find(([k]) => normalizeHeaderKey(k) === want);
    if (hit && hit[1] !== undefined && hit[1] !== null) {
      return hit[1];
    }
  }
  return '';
};

const toStringValue = (value) => {
  if (value == null) return '';
  if (typeof value === 'string') return value.trim();
  return String(value).trim();
};

const toStringArray = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => toStringValue(item)).filter(Boolean);
  }
  if (typeof value === 'string') {
    return value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return [];
};

const inferProcessName = (row) => {
  const direct = toStringValue(
    getFirstValue(row, [
      'name',
      'processName',
      'name',
      'process',
      'process_title',
      'processTitle',
      'process description'
    ])
  );
  if (direct) return direct;

  const entries = Object.entries(row || {});
  const dynamicKeyMatch = entries.find(([key, value]) => {
    const normalizedKey = String(key || '').toLowerCase().replace(/[_\s-]/g, '');
    if (!normalizedKey.includes('process') || !normalizedKey.includes('name')) return false;
    return Boolean(toStringValue(value));
  });
  return dynamicKeyMatch ? toStringValue(dynamicKeyMatch[1]) : '';
};

function ProcessUploadPage() {
  const { cycleId } = useParams();
  const navigate = useNavigate();
  const [selectedFile, setSelectedFile] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [isValidating, setIsValidating] = useState(false);

  const onSelectFile = (event) => {
    const file = event.target.files?.[0];
    setSelectedFile(file || null);
    setValidationResult(null);
  };

  const ensureSupportedExtension = (file) => {
    const extension = file?.name?.split('.').pop()?.toLowerCase() || '';
    return SUPPORTED_EXTENSIONS.includes(extension);
  };

  const handleValidateFile = async () => {
    if (!cycleId) {
      toast.error('Cycle ID missing. Please go back and select a cycle.');
      return;
    }
    if (!selectedFile) {
      toast.error('Please select an Excel or CSV file.');
      return;
    }
    if (!ensureSupportedExtension(selectedFile)) {
      toast.error('Unsupported file type. Use .xlsx or .csv');
      return;
    }

    try {
      setIsValidating(true);
      setValidationResult(null);

      const userHash = localStorage.getItem('userHash') || '';
      const [uploaded] = await uploadFilesAndComplete([selectedFile], userHash, MODULE_UPLOAD.SOX_PROCESS);

      if (!uploaded?.file_id) {
        throw new Error('Upload completed but file ID was not returned.');
      }

      const validationResponse = await axiosInstance.post('/api/process-upload/validate', {
        file_id: uploaded.file_id,
        sox_cycle: cycleId
      });

      const result = validationResponse.data;
      setValidationResult(result);
      if (result?.header_valid) {
        if (Number(result?.failed_rows || 0) > 0) {
          toast.error('Validation found row errors. Review rows on the create page.');
        } else {
          toast.success('Validation passed. Continue on the create page.');
        }
        navigate(soxProcessCreatePath(cycleId), { state: { tab: 'upload' } });
      } else {
        toast.error(
          'Header validation failed. Please verify the file headers match the sample document and try again.'
        );
      }
    } catch (error) {
      console.error('Process file validation failed:', error);
      setValidationResult(null);
      toast.error(resolve403ErrorMessage(error, error.response?.data?.error || error.message || 'Failed to validate file.'));
    } finally {
      setIsValidating(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="mb-6 flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate('/sox-dashboard')}
            className="text-gray-600 hover:text-gray-900"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Upload Processes</h1>
            <p className="text-sm text-gray-500">
              Upload an Excel/CSV file, validate it, then create processes for this cycle.
            </p>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <div className="mb-4">
            <label className="text-sm font-medium text-gray-700 block mb-2">Choose file (.xlsx, .csv)</label>
            <input
              type="file"
              accept=".xlsx,.csv"
              onChange={onSelectFile}
              className="block w-full text-sm text-gray-700"
            />
            <p className="mt-2 text-xs text-gray-500">Only `.xlsx` and `.csv` are supported.</p>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={handleValidateFile}
              disabled={!selectedFile || isValidating}
              className="inline-flex items-center gap-2 rounded-lg bg-gray-900 text-white px-4 py-2 text-sm font-medium hover:bg-gray-800 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isValidating ? <Loader className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
              Validate File
            </button>
          </div>
        </div>

        {validationResult && (
          <div className="mt-6 bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Validation Result</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
              <div className="rounded-lg bg-gray-50 p-3">
                <p className="text-xs text-gray-500">Total Rows</p>
                <p className="text-xl font-semibold text-gray-900">{validationResult.total_rows ?? 0}</p>
              </div>
              <div className="rounded-lg bg-green-50 p-3">
                <p className="text-xs text-green-700">Passed Rows</p>
                <p className="text-xl font-semibold text-green-700">{validationResult.passed_rows ?? 0}</p>
              </div>
              <div className="rounded-lg bg-red-50 p-3">
                <p className="text-xs text-red-700">Failed Rows</p>
                <p className="text-xl font-semibold text-red-700">{validationResult.failed_rows ?? 0}</p>
              </div>
            </div>

            {!(validationResult.header_valid && Number(validationResult.failed_rows || 0) === 0) ? (
              <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="h-4 w-4" />
                  <span className="font-medium">Validation issues found</span>
                </div>
                <p>
                  Fix the file and upload again. Header valid: {String(Boolean(validationResult.header_valid))}
                </p>
              </div>
            ) : (
              <div className="rounded-lg border border-green-300 bg-green-50 p-4 text-sm text-green-800">
                <div className="flex items-center gap-2">
                  <span className="font-medium">All checks passed. Redirecting to staging/review screen.</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default ProcessUploadPage;
